import { Fragment, useCallback, useEffect, useState } from "react";
import { builtinFormatters, useSignals } from "cayo.ui";
import { OdataQuery } from "odata";
import styled from "styled-components";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { IJsonForm, HttpMethods, ActionParameters } from "@cayo/shared/api/types/schema";
import { appSignals, showActionParams } from "@cayo/shared/components/App/utils";
import logger from "@cayo/shared/libs/logger";
import useService from "@cayo/shared/services/hooks/useService";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import PropertiesDialog from "../../admin-components/dialogs/PropertiesDialog";
import { ActionContext } from "../../api/schema.api";
import ajax from "@cayo/shared/libs/ajax";
import { ApiPath } from "../../utils/api-path";
import CollectActionParametersDialog from "../Dialogs/CollectActionParametersDialog";
import ReportDialog from "../Dialogs/ReportDialog";

const guidRegex =
  /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}|[0-9a-fA-F]{8}[0-9a-fA-F]{4}[0-9a-fA-F]{4}[0-9a-fA-F]{4}[0-9a-fA-F]{12}/gi;

const ActionDialogHolder = () => {
  const [actionParams, setActionParams] = useState<showActionParams>();
  const [action, setAction] = useState<{
    scheme: IJsonForm;
    object?: Entity;
    adminProps?: boolean;
    template?: string;
  }>();
  const actionBuilder = useService("actionBuilder");

  const { uiInteraction } = actionBuilder;

  const onClose = useCallback(() => {
    setAction(undefined);
    setActionParams(undefined);
  }, [setAction, setActionParams]);

  useEffect(() => {
    if (!actionParams?.schemeId) {
      return;
    }

    const loadScheme = async () => {
      try {
        uiInteraction.setLoading(true);

        let object: any = undefined;

        // handle 'Update Now' & 'Schedule Update' actions (static alerts)
        const isEntityAction = !!actionParams.schemeId!.match(guidRegex)?.length;
        const parameters = actionParams.actionParameters as ActionParameters;
        const isPostAction = !!parameters;
        let query: OdataQuery | undefined;
        let method: HttpMethods = "GET";
        let body: ActionContext | undefined;

        const adminProps = actionParams.schemeId?.endsWith("/Properties") && isPostAction;
        if (isPostAction) {
          method = "POST";
          const selectedObjects = actionParams.objects as any[];
          body = {
            selectedIds: selectedObjects?.map((o) => o[parameters.objectIdPropName!]),
            selectedNames: selectedObjects?.map((o) => o[parameters.objectNamePropName!]),
          };
        } else {
          query = {
            independent: actionParams.independent,
          };
        }

        if (!actionParams?.objects?.length && isEntityAction && !isPostAction) {
          const objectPath = builtinFormatters.cutLastSegment(actionParams.schemeId!);

          object = await ajax.fetchFn(new ApiPath(objectPath).toString());
          logger.debug(`object loaded for scheme ${actionParams.schemeId}`, object);
        }

        const scheme = await ajax.getClient(
          "scheme/action/" + actionParams?.schemeId,
          method,
          undefined,
          body,
          query
        );

        const { model, ...rawScheme } = scheme;

        setAction({
          scheme: rawScheme,
          object: object ?? model,
          adminProps: adminProps || (scheme as IJsonForm).isWizard,
          template: (scheme as IJsonForm).template,
        });
      } catch (e) {
        onClose();
        ajaxUtils.handleError(e);
      } finally {
        uiInteraction.setLoading(false);
      }
    };

    loadScheme();
  }, [actionParams?.schemeId, onClose]);

  useEffect(() => {
    return appSignals.subscribe("showAction", (prms) => {
      if (!prms) {
        onClose();
        return;
      }
      setActionParams(prms);
    });
  }, [onClose]);

  const signals = useSignals<"close">([{ s: "close", handler: onClose }]);

  if (!action) {
    return <Fragment />;
  }

  const Comp: any = action?.adminProps ? PropertiesDialog : CollectActionParametersDialog;
  // TODO: move to map [template name] -> component
  if (action.template?.toLowerCase() === "report") {
    return <ReportDialog scheme={action.scheme} onClose={onClose} />;
  }

  return (
    <ActionDialogHolderContainer>
      <Comp
        onClose={onClose}
        actionBuilder={actionBuilder}
        {...action.scheme}
        actionParameters={actionParams?.actionParameters}
        externalObjects={actionParams?.objects || (action.object ? [action.object] : undefined)}
        signals={signals as any}
        onSaveComplete={actionParams?.onSaveComplete}
        isAdminProps={action.adminProps}
        object={action.object}
      />
    </ActionDialogHolderContainer>
  );
};

const ActionDialogHolderContainer = styled.div`
  position: relative;
`;

export default ActionDialogHolder;
